import { useCallback, useEffect, useMemo, useState } from 'react';

// APIs
import * as DataListAPI from '../../../../api/dataList';
import * as ProdutoAPI from '../../../../api/produto';

/* utils */
import { formatarNumeroDecimal, formatarNumeroDecimal3Digitos } from '../../../../utils/masks';
/**
 * useIndustriaModule (Produto)
 * - Cores (grid + CRUD)
 * - Produto Bobina (datalist -> produtoBobinaId)
 *
 * Requisitos:
 * - ProdutoAPI.listarCores({ produtoId, start, size, ... }) OU listarCores(produtoId, ...)
 * - ProdutoAPI.createOrEditCor(payload)
 * - DataListAPI.dataList({ empresaId, tabela, filtro })
 */
export function useIndustriaModule({
    formData,
    dados, // state do cabecalho (produto)
    setDados, // setter do cabecalho
    parametro,
    profile,
    // MRT state (centraliza aqui)
    mrt,

    setFlashMessage,

    // helpers opcionais
    pickSelectedId, // (value, result, field="descricao") => id
    OptionsEmbalagemProducao,
}) {
    // extrai MRT state (evita undefined)
    const pagination = mrt?.pagination ?? { pageIndex: 0, pageSize: 5 };
    const columnFilters = mrt?.columnFilters ?? [];
    const columnFilterFns = mrt?.columnFilterFns ?? [];
    const globalFilter = mrt?.globalFilter ?? '';
    const sorting = mrt?.sorting ?? [];

    // -------------------------
    // helpers
    // -------------------------
    const consultaDatalist = useCallback(
        async (params) => {
            try {
                return await DataListAPI.dataList(params);
            } catch (e) {
                console.error('Erro ao consultar DataList:', e);
                throw e;
            }
        },
        [DataListAPI]
    );

    const getIdByValueDom = useCallback((value) => {
        return document.querySelector(`datalist option[value="${value}"]`)?.dataset.id || null;
    }, []);

    const pickId = useCallback(
        (value, result, field = 'descricao') => {
            if (typeof pickSelectedId === 'function') return pickSelectedId(value, result, field);
            const found = (result || []).find((x) => String(x?.[field] || '') === String(value || ''));
            return found?.id ?? null;
        },
        [pickSelectedId]
    );

    // ------------------------------------------
    // state: bobina/anilox/cor/anilox (datalist)
    // ------------------------------------------
    const [ProdutoBobina, setProdutoBobina] = useState({});
    const [OptionsProdutoBobina, setOptionsProdutoBobina] = useState([]);
    const [Cilindro, setCilindro] = useState({});
    const [OptionsCilindro, setOptionsCilindro] = useState([]);
    const [Cor, setCor] = useState({});
    const [Anilox, setAnilox] = useState({});
    const [OptionsCores, setOptionsCores] = useState([]);
    const [OptionsAnilox, setOptionsAnilox] = useState([]);

    // -------------------------
    // state: grid de cores
    // -------------------------
    const [produtoCores, setProdutoCores] = useState([]);
    const [rowCountCor, setRowCountCor] = useState(0);

    const baseNovaCor = useMemo(
        () => ({
            empresaId: dados?.empresaId ?? null,
            produtoId: dados?.id ?? null,
            uuid: null,
            ordem: null,
            corId: null,
            aniloxId: null,
            Cor: {},
            Anilox: {},
            ativo: true,
            funcao: 'Adicionar',
            index: null,
        }),
        [dados?.empresaId, dados?.id]
    );

    const [novaCor, setNovaCor] = useState(baseNovaCor);

    // mantém defaults quando muda produto/empresa
    useEffect(() => {
        setNovaCor((prev) => ({
            ...baseNovaCor,
            // mantém funcao/uuid se você estiver editando algo? melhor resetar:
            funcao: 'Adicionar',
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseNovaCor]);

    // -------------------------
    // actions: bobina datalist
    // -------------------------
    const changeProdutoBobina = useCallback(
        async (value) => {
            setProdutoBobina((prev) => ({ ...prev, descricao: value }));

            const result = await consultaDatalist({
                empresaId: dados?.empresaId,
                tabela: 'Produto',
                condicao: 'isBobina',
                filtro: value,
            });

            setOptionsProdutoBobina(result);

            const selectedId = getIdByValueDom(value) || pickId(value, result, 'descricao');

            setDados((prev) => ({
                ...prev,
                produtoBobinaId: selectedId ?? null,
            }));

            // mantém texto (se achou o objeto)
            const selected = (result || []).find((x) => String(x?.id) === String(selectedId));

            setProdutoBobina((prev) => ({
                ...prev,
                id: selected?.id ?? selectedId ?? null,
                descricao: selected?.descricao ?? value ?? null,
                label: selected?.label ?? '',
            }));

            return selectedId;
        },
        [consultaDatalist, dados?.empresaId, getIdByValueDom, pickId, setDados]
    );

    // -------------------------
    // actions: cilindro datalist
    // -------------------------
    const changeCilindro = useCallback(
        async (value) => {
            setCilindro((prev) => ({ ...prev, descricao: value }));

            const result = await consultaDatalist({
                empresaId: dados?.empresaId,
                tabela: 'Cilindro',
                filtro: value,
            });

            setOptionsCilindro(result);

            const selectedId = getIdByValueDom(value) || pickId(value, result, 'descricao');

            setDados((prev) => ({
                ...prev,
                cilindroId: selectedId ?? null,
            }));

            // mantém texto (se achou o objeto)
            const selected = (result || []).find((x) => String(x?.id) === String(selectedId));
            if (selected) setCilindro(selected);

            return selectedId;
        },
        [consultaDatalist, dados?.empresaId, getIdByValueDom, pickId, setDados]
    );

    // -------------------------
    // actions: cor/anilox datalist
    // -------------------------
    const changeCorDescricao = useCallback(
        async (value) => {
            setCor((prev) => ({ ...prev, descricao: value }));

            const result = await consultaDatalist({
                empresaId: dados?.empresaId,
                tabela: 'Cor',
                filtro: value,
            });

            setOptionsCores(result || []);

            const selectedId = getIdByValueDom(value) || pickId(value, result, 'descricao');
            const selected = (result || []).find((x) => String(x?.id) === String(selectedId));

            setNovaCor((prev) => ({
                ...prev,
                corId: selectedId ?? null,
                Cor: selected ?? prev.Cor,
            }));

            if (selected) setCor(selected);

            return selectedId;
        },
        [consultaDatalist, dados?.empresaId, getIdByValueDom, pickId]
    );

    const changeAniloxDescricao = useCallback(
        async (value) => {
            setAnilox((prev) => ({ ...prev, descricao: value }));

            const result = await consultaDatalist({
                empresaId: dados?.empresaId,
                tabela: 'Anilox',
                filtro: value,
            });

            setOptionsAnilox(result || []);

            const selectedId = getIdByValueDom(value) || pickId(value, result, 'descricao');
            const selected = (result || []).find((x) => String(x?.id) === String(selectedId));

            setNovaCor((prev) => ({
                ...prev,
                aniloxId: selectedId ?? null,
                Anilox: selected ?? prev.Anilox,
            }));

            if (selected) setAnilox(selected);

            return selectedId;
        },
        [consultaDatalist, dados?.empresaId, getIdByValueDom, pickId]
    );

    // -------------------------
    // actions: cor (draft)
    // -------------------------
    const limparCor = useCallback(() => {
        setNovaCor(baseNovaCor);
        setCor({});
        setAnilox({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseNovaCor]);

    const selectCorRow = useCallback(
        (originalRow, rowIndex, funcao) => {
            const newData = {
                uuid: originalRow.uuid,
                Cor: originalRow.Cor,
                Anilox: originalRow.Anilox,
                Empresa: originalRow.Empresa,

                empresaId: originalRow.empresaId || dados?.empresaId,
                produtoId: originalRow.produtoId || dados?.id,
                ordem: originalRow.ordem,
                corId: originalRow.corId,
                aniloxId: originalRow.aniloxId,

                ativo: originalRow.ativo,
                funcao,
                index: rowIndex,
                id: originalRow.id, // se existir
            };

            setNovaCor(newData);
            setCor(newData.Cor || {});
            setAnilox(newData.Anilox || {});
        },
        [dados?.empresaId, dados?.id]
    );

    // -------------------------
    // remote fetch (cores)
    // -------------------------
    const refresh = useCallback(
        async ({ start = 0, size = 50 } = {}) => {
            if (!dados?.id) {
                // sem produto salvo: usa local
                setRowCountCor(produtoCores?.length || 0);
                return;
            }

            try {
                await ProdutoAPI.listarCores({
                    start: pagination.pageIndex * pagination.pageSize,
                    size: pagination.pageSize,
                    filters: JSON.stringify(columnFilters ?? []),
                    filtersMode: JSON.stringify(columnFilterFns ?? []),
                    globalFilter: globalFilter ?? '',
                    sorting: JSON.stringify(sorting ?? []),
                    produtoId: dados.id ?? null,
                }).then((response) => {
                    setProdutoCores(response.data.data);
                    setRowCountCor(response.data.meta.totalRowCount);
                });
            } catch (e) {
                console.error(e);
                setFlashMessage?.('Erro ao carregar cores.', 'error');
            }
        },
        [ProdutoAPI, dados?.id, produtoCores?.length, setFlashMessage]
    );

    // -------------------------
    // save cor
    // -------------------------
    const saveCor = useCallback(async () => {
        const payload = {
            ...novaCor,
            empresaId: novaCor.empresaId ?? dados?.empresaId,
            produtoId: novaCor.produtoId ?? dados?.id,
        };

        // 1) se já tem produto salvo -> backend
        if (dados?.id) {
            try {
                const result = await ProdutoAPI.createOrEditCor(payload);

                setFlashMessage?.(result?.message || 'Ok', result?.type || 'success');

                if (result?.type === 'success') {
                    // se API devolve lista pronta:
                    if (Array.isArray(result?.data)) {
                        setProdutoCores(result.data);
                        setRowCountCor(result.data.length);
                    } else {
                        await refresh();
                    }
                }
            } catch (e) {
                console.error(e);
                setFlashMessage?.('Erro ao salvar cor.', 'error');
            } finally {
                limparCor();
            }
            return;
        }

        // 2) sem produto salvo -> local (igual seu código)
        setProdutoCores((prev) => {
            const list = Array.isArray(prev) ? [...prev] : [];
            const f = (novaCor?.funcao || '').toLowerCase();

            if (f === 'editar') {
                return list.map((item, i) => (i === novaCor.index ? { ...item, ...payload } : item));
            }
            if (f === 'deletar') {
                return list.filter((item) => item.id !== novaCor.id && item.uuid !== novaCor.uuid);
            }
            return [...list, payload];
        });

        limparCor();
    }, [ProdutoAPI, dados?.empresaId, dados?.id, refresh, limparCor, novaCor, setFlashMessage]);

    // -------------------------
    // change handler único (Form pode usar se quiser)
    // -------------------------
    const change = useCallback(
        async (e) => {
            const { name, value, type, checked } = e.target;
            const v = type === 'checkbox' ? !!checked : value;

            // draft cor
            if (name === 'novaCor.ordem') {
                setNovaCor((prev) => ({ ...prev, ordem: v }));
                return;
            }
            if (name === 'novaCor.ativo') {
                setNovaCor((prev) => ({ ...prev, ativo: !!checked }));
                return;
            }
            if (name === 'novaCor.Cor.descricao') {
                await changeCorDescricao(v);
                return;
            }
            if (name === 'novaCor.Anilox.descricao') {
                await changeAniloxDescricao(v);
                return;
            }

            // bobina: produto bobina
            if (name === 'ProdutoBobina.descricao') {
                await changeProdutoBobina(v);
                return;
            }

            // cilindro:
            if (name === 'Cilindro.descricao') {
                await changeCilindro(v);
                return;
            }
            // fallback: grava no dados se quiser (opcional)
            // campos numericos
            const numberFields = ['producaoMinima'];

            if (numberFields.includes(name)) {
                //const valorFormatado = formatarNumeroDecimal(v);
                const valorFormatado = formatarNumeroDecimal3Digitos(v);

                setDados((prev) => {
                    const next = { ...prev, [name]: valorFormatado };
                    return { ...next };
                });
                return;
            }

            setDados((prev) => ({ ...prev, [name]: v }));
        },
        [changeAniloxDescricao, changeCorDescricao, changeProdutoBobina, setDados]
    );

    const focus = useCallback(() => {}, []);
    const blur = useCallback(() => {}, []);

    const setRemote = useCallback((next) => {
        const pb = next?.ProdutoBobina || next?.produtoBobina || null;

        if (pb?.id || pb?.descricao) {
            setProdutoBobina((prev) => ({
                ...prev,
                ...pb,
                descricao: pb?.descricao ?? prev?.descricao ?? '',
            }));
        } else if (next?.produtoBobinaId) {
            setProdutoBobina((prev) => ({
                ...prev,
                id: next.produtoBobinaId,
                descricao: prev?.descricao ?? '',
            }));
        }

        const cores = next?.produtoCores || next?.ProdutoCores || [];
        if (Array.isArray(cores)) {
            setProdutoCores(cores);
            setRowCountCor(cores.length);
        }
    }, []);

    useEffect(() => {
        if (formData) setRemote(formData);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    return {
        state: {
            dados,
            parametro,

            ProdutoBobina,
            OptionsProdutoBobina,
            Cilindro,
            OptionsCilindro,
            OptionsEmbalagemProducao,
            Cor,
            OptionsCores,
            Anilox,
            OptionsAnilox,

            novaCor,
            produtoCores,
            rowCountCor,
        },
        actions: {
            change,
            focus,
            blur,
            setRemote,

            // bobina
            changeProdutoBobina,

            // cores
            refresh,
            saveCor,
            limparCor,
            selectCorRow,

            // datalist diretos se precisar
            changeCorDescricao,
            changeAniloxDescricao,
        },
    };
}
